// Symbol sets for the copy-and-paste symbol pages. Plain Unicode, no images.
// Each entry carries the codepoint so the pages can show the alt code and the
// official Unicode name, which is what people search for alongside the glyph.

export type SymbolItem = {
  char: string;
  name: string;
  /** Uppercase hex codepoint, no U+ prefix. */
  code: string;
};

export type SymbolGroup = {
  label: string;
  items: SymbolItem[];
};

export type SymbolSetKey =
  | 'all'
  | 'degree'
  | 'heart'
  | 'check'
  | 'infinity'
  | 'arrow'
  | 'star'
  | 'currency'
  | 'math';

const degree: SymbolGroup = {
  label: 'Degree and temperature',
  items: [
    { char: '°', name: 'Degree sign', code: '00B0' },
    { char: '℃', name: 'Degree Celsius', code: '2103' },
    { char: '℉', name: 'Degree Fahrenheit', code: '2109' },
    { char: 'º', name: 'Masculine ordinal indicator', code: '00BA' },
    { char: 'ª', name: 'Feminine ordinal indicator', code: '00AA' },
    { char: '∠', name: 'Angle', code: '2220' },
    { char: '⊾', name: 'Right angle with arc', code: '22BE' },
    { char: '′', name: 'Prime (minutes of arc)', code: '2032' },
    { char: '″', name: 'Double prime (seconds of arc)', code: '2033' },
    { char: 'K', name: 'Kelvin (plain letter, preferred)', code: '004B' },
  ],
};

const heart: SymbolGroup = {
  label: 'Hearts',
  items: [
    { char: '♥', name: 'Black heart suit', code: '2665' },
    { char: '♡', name: 'White heart suit', code: '2661' },
    { char: '❤', name: 'Heavy black heart', code: '2764' },
    { char: '❥', name: 'Rotated heavy black heart bullet', code: '2765' },
    { char: '❣', name: 'Heavy heart exclamation mark', code: '2763' },
    { char: '💕', name: 'Two hearts', code: '1F495' },
    { char: '💖', name: 'Sparkling heart', code: '1F496' },
    { char: '💗', name: 'Growing heart', code: '1F497' },
    { char: '💘', name: 'Heart with arrow', code: '1F498' },
    { char: '💙', name: 'Blue heart', code: '1F499' },
    { char: '💚', name: 'Green heart', code: '1F49A' },
    { char: '💛', name: 'Yellow heart', code: '1F49B' },
    { char: '🖤', name: 'Black heart', code: '1F5A4' },
    { char: '🤍', name: 'White heart', code: '1F90D' },
    { char: '💔', name: 'Broken heart', code: '1F494' },
    { char: '♥️', name: 'Heart suit, emoji presentation', code: '2665 FE0F' },
  ],
};

const check: SymbolGroup = {
  label: 'Check marks and crosses',
  items: [
    { char: '✓', name: 'Check mark', code: '2713' },
    { char: '✔', name: 'Heavy check mark', code: '2714' },
    { char: '☑', name: 'Ballot box with check', code: '2611' },
    { char: '✅', name: 'White heavy check mark', code: '2705' },
    { char: '🗸', name: 'Light check mark', code: '1F5F8' },
    { char: '✗', name: 'Ballot X', code: '2717' },
    { char: '✘', name: 'Heavy ballot X', code: '2718' },
    { char: '☒', name: 'Ballot box with X', code: '2612' },
    { char: '❌', name: 'Cross mark', code: '274C' },
    { char: '❎', name: 'Negative squared cross mark', code: '274E' },
    { char: '☐', name: 'Empty ballot box', code: '2610' },
    { char: '✖', name: 'Heavy multiplication X', code: '2716' },
  ],
};

const infinity: SymbolGroup = {
  label: 'Infinity and continuation',
  items: [
    { char: '∞', name: 'Infinity', code: '221E' },
    { char: '♾', name: 'Permanent paper sign (infinity emoji)', code: '267E' },
    { char: '∝', name: 'Proportional to', code: '221D' },
    { char: '∫', name: 'Integral', code: '222B' },
    { char: '⧜', name: 'Incomplete infinity', code: '29DC' },
    { char: '⧝', name: 'Tie over infinity', code: '29DD' },
    { char: '⧞', name: 'Infinity negated with vertical bar', code: '29DE' },
    { char: '…', name: 'Horizontal ellipsis', code: '2026' },
    { char: '⋯', name: 'Midline horizontal ellipsis', code: '22EF' },
  ],
};

const arrow: SymbolGroup = {
  label: 'Arrows',
  items: [
    { char: '←', name: 'Leftwards arrow', code: '2190' },
    { char: '↑', name: 'Upwards arrow', code: '2191' },
    { char: '→', name: 'Rightwards arrow', code: '2192' },
    { char: '↓', name: 'Downwards arrow', code: '2193' },
    { char: '↔', name: 'Left right arrow', code: '2194' },
    { char: '↕', name: 'Up down arrow', code: '2195' },
    { char: '↖', name: 'North west arrow', code: '2196' },
    { char: '↗', name: 'North east arrow', code: '2197' },
    { char: '↘', name: 'South east arrow', code: '2198' },
    { char: '↙', name: 'South west arrow', code: '2199' },
    { char: '⇐', name: 'Leftwards double arrow', code: '21D0' },
    { char: '⇒', name: 'Rightwards double arrow', code: '21D2' },
    { char: '⇔', name: 'Left right double arrow', code: '21D4' },
    { char: '⇧', name: 'Upwards white arrow (shift key)', code: '21E7' },
    { char: '⌫', name: 'Erase to the left (backspace)', code: '232B' },
    { char: '⏎', name: 'Return symbol', code: '23CE' },
    { char: '➔', name: 'Heavy wide-headed rightwards arrow', code: '2794' },
    { char: '➤', name: 'Black rightwards arrowhead', code: '27A4' },
    { char: '↺', name: 'Anticlockwise open circle arrow', code: '21BA' },
    { char: '↻', name: 'Clockwise open circle arrow', code: '21BB' },
  ],
};

const star: SymbolGroup = {
  label: 'Stars and sparkles',
  items: [
    { char: '★', name: 'Black star', code: '2605' },
    { char: '☆', name: 'White star', code: '2606' },
    { char: '✦', name: 'Black four pointed star', code: '2726' },
    { char: '✧', name: 'White four pointed star', code: '2727' },
    { char: '✩', name: 'Stress outlined white star', code: '2729' },
    { char: '✪', name: 'Circled white star', code: '272A' },
    { char: '✫', name: 'Open centre black star', code: '272B' },
    { char: '✬', name: 'Black centre white star', code: '272C' },
    { char: '✮', name: 'Heavy outlined black star', code: '272E' },
    { char: '✯', name: 'Pinwheel star', code: '272F' },
    { char: '⭐', name: 'White medium star', code: '2B50' },
    { char: '🌟', name: 'Glowing star', code: '1F31F' },
    { char: '✨', name: 'Sparkles', code: '2728' },
    { char: '❋', name: 'Heavy eight pointed pinwheel star', code: '274B' },
  ],
};

const currency: SymbolGroup = {
  label: 'Currency',
  items: [
    { char: '$', name: 'Dollar sign', code: '0024' },
    { char: '€', name: 'Euro sign', code: '20AC' },
    { char: '£', name: 'Pound sign', code: '00A3' },
    { char: '¥', name: 'Yen sign', code: '00A5' },
    { char: '₹', name: 'Indian rupee sign', code: '20B9' },
    { char: '₽', name: 'Ruble sign', code: '20BD' },
    { char: '₩', name: 'Won sign', code: '20A9' },
    { char: '₺', name: 'Turkish lira sign', code: '20BA' },
    { char: '¢', name: 'Cent sign', code: '00A2' },
    { char: '₿', name: 'Bitcoin sign', code: '20BF' },
  ],
};

const math: SymbolGroup = {
  label: 'Math and technical',
  items: [
    { char: '±', name: 'Plus-minus sign', code: '00B1' },
    { char: '×', name: 'Multiplication sign', code: '00D7' },
    { char: '÷', name: 'Division sign', code: '00F7' },
    { char: '≈', name: 'Almost equal to', code: '2248' },
    { char: '≠', name: 'Not equal to', code: '2260' },
    { char: '≤', name: 'Less-than or equal to', code: '2264' },
    { char: '≥', name: 'Greater-than or equal to', code: '2265' },
    { char: '√', name: 'Square root', code: '221A' },
    { char: 'π', name: 'Greek small letter pi', code: '03C0' },
    { char: 'Σ', name: 'Greek capital letter sigma', code: '03A3' },
    { char: 'Δ', name: 'Greek capital letter delta', code: '0394' },
    { char: 'µ', name: 'Micro sign', code: '00B5' },
    { char: '‰', name: 'Per mille sign', code: '2030' },
    { char: '²', name: 'Superscript two', code: '00B2' },
    { char: '³', name: 'Superscript three', code: '00B3' },
    { char: '½', name: 'Vulgar fraction one half', code: '00BD' },
  ],
};

const punctuation: SymbolGroup = {
  label: 'Punctuation and marks',
  items: [
    { char: '•', name: 'Bullet', code: '2022' },
    { char: '◦', name: 'White bullet', code: '25E6' },
    { char: '▪', name: 'Black small square', code: '25AA' },
    { char: '·', name: 'Middle dot', code: '00B7' },
    { char: '§', name: 'Section sign', code: '00A7' },
    { char: '¶', name: 'Pilcrow sign', code: '00B6' },
    { char: '©', name: 'Copyright sign', code: '00A9' },
    { char: '®', name: 'Registered sign', code: '00AE' },
    { char: '™', name: 'Trade mark sign', code: '2122' },
    { char: '†', name: 'Dagger', code: '2020' },
    { char: '‡', name: 'Double dagger', code: '2021' },
    { char: '¿', name: 'Inverted question mark', code: '00BF' },
    { char: '¡', name: 'Inverted exclamation mark', code: '00A1' },
    { char: '«', name: 'Left-pointing double angle quotation mark', code: '00AB' },
    { char: '»', name: 'Right-pointing double angle quotation mark', code: '00BB' },
  ],
};

const SETS: Record<Exclude<SymbolSetKey, 'all'>, SymbolGroup[]> = {
  degree: [degree, math],
  heart: [heart, star],
  check: [check, punctuation],
  infinity: [infinity, math],
  arrow: [arrow, punctuation],
  star: [star, heart],
  currency: [currency, math],
  math: [math, currency],
};

const ALL_GROUPS: SymbolGroup[] = [
  heart,
  star,
  arrow,
  check,
  degree,
  infinity,
  math,
  currency,
  punctuation,
];

export function getSymbolGroups(key: SymbolSetKey): SymbolGroup[] {
  return key === 'all' ? ALL_GROUPS : SETS[key];
}

/** The single glyph a one-symbol page is about, shown large above the grid. */
export const FEATURED_SYMBOL: Partial<Record<SymbolSetKey, SymbolItem>> = {
  degree: degree.items[0],
  heart: heart.items[0],
  check: check.items[0],
  infinity: infinity.items[0],
  arrow: arrow.items[2],
  star: star.items[0],
};

// Build the server-rendered reference table for a symbol page.
//
// Why this exists, measured 2026-09-19 over 28 days: the single-symbol tool pages carry ~26,000
// impressions and return 111 clicks, a 0.43% CTR, while /blog/alt-codes-list-reference returns
// 2.16% from an almost identical average position. The controlled comparison is
// /tools/check-mark-symbol at position 13.1 earning 0.22% against alt-codes at position 14.0
// earning 2.16%. Position is not the variable; the shape of the answer is. A single glyph fits in
// a search snippet and the click never happens, so these pages need the thing a snippet cannot
// hold: every code for every symbol in the set, in one table.
export function buildSymbolReferenceTable(key: SymbolSetKey): {
  caption: string;
  head: string[];
  rows: string[][];
} {
  const rows: string[][] = [];
  const seen = new Set<string>();

  for (const group of getSymbolGroups(key)) {
    for (const item of group.items) {
      if (seen.has(item.char)) continue;
      seen.add(item.char);
      const decimal = item.char.codePointAt(0) ?? 0;
      rows.push([
        item.char,
        item.name,
        `U+${item.code}`,
        `&#${decimal};`,
        // Alt codes only exist for characters Windows can enter that way. Above the BMP there is
        // no alt code, and printing one would be a wrong answer stated confidently.
        decimal <= 0xffff ? `Alt + ${decimal}` : 'No alt code',
        `\\${item.code}`,
      ]);
    }
  }

  return {
    caption: 'Every code for each symbol: copy the character, or type it from the code.',
    head: ['Symbol', 'Unicode name', 'Code point', 'HTML', 'Windows alt code', 'CSS'],
    rows,
  };
}
